import clsx from 'clsx';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHotkeys, useHotkeysContext } from 'react-hotkeys-hook';

import { Icon } from '../Icon/BaseIcon/Icon';

type Props = {
  value?: string;
  options: string[];
  optionTitles?: React.ReactNode[];
  placeholder: string;
  onChange: (value: string) => void;
  'data-testid'?: string;
  disabled?: boolean;
};

export const ModalDropdown = ({
  'data-testid': testId = 'modal-dropdown',
  value,
  placeholder,
  options,
  optionTitles,
  onChange,
  disabled,
}: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const { disableScope, enableScope } = useHotkeysContext();
  const resetHotkeyScope = useCallback(() => {
    disableScope('dropdown');
    enableScope('modal');
  }, [enableScope, disableScope]);

  useEffect(() => {
    if (isOpen) {
      disableScope('modal');
      enableScope('dropdown');
    } else {
      resetHotkeyScope();
    }
    return () => {
      resetHotkeyScope();
    };
  }, [isOpen, enableScope, disableScope, resetHotkeyScope]);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const onBlur = useCallback((evt?: React.FocusEvent<HTMLElement>) => {
    if (evt && evt.relatedTarget) {
      if (dropdownRef.current?.contains(evt.relatedTarget as Node)) {
        evt.preventDefault();
        return;
      }
    }
    setIsOpen(false);
  }, []);

  useHotkeys(
    ['esc'],
    (evt) => {
      evt.preventDefault();
      onBlur();
    },
    {
      enableOnFormTags: true,
      scopes: ['dropdown'],
    },
    []
  );

  let valueToDisplay: string | React.ReactNode = value || placeholder;
  if (optionTitles && optionTitles.length === options.length && value) {
    valueToDisplay = optionTitles[options.indexOf(value)];
  }

  return (
    <div data-testid={testId} className="w-full h-full min-h-f2 z-30">
      <button
        type="button"
        disabled={disabled}
        className={clsx(
          'flex flex-row w-full h-f2 justify-between items-center',
          'bg-main-200 hover:bg-negative focus:bg-negative',
          (!value || disabled) && 'text-main-500',
          value && 'text-main'
        )}
        onClick={() => setIsOpen(!isOpen)}
        onBlur={onBlur}
      >
        {valueToDisplay}
        <Icon type="select" size="md" primaryColor="gray" />
      </button>
      <div
        ref={dropdownRef}
        data-testid="list-of-options"
        className={clsx(
          'overflow-y-scroll scrollbar-hide flex flex-col w-full',
          'transition-[max-height,opacity] duration-200 ease-in-out',
          isOpen ? 'max-h-f10 opacity-100' : 'max-h-0 opacity-0'
        )}
      >
        {options.map((option, index) => (
          <button
            type="button"
            key={option}
            className={clsx(
              'text-left h-f2 cursor-pointer w-full',
              'bg-main-200 hover:bg-negative',
              value === option ? 'text-main-500' : 'text-main',
              'border-t-[1px] border-canvas box-border'
            )}
            onClick={() => {
              onChange(option);
              setIsOpen(false);
            }}
          >
            {optionTitles && optionTitles.length === options.length
              ? optionTitles[index]
              : option}
          </button>
        ))}
      </div>
    </div>
  );
};
